function cleanEmptyInlineTags(html: string) {
  let next = html;
  let previous = "";

  while (next !== previous) {
    previous = next;
    next = next.replace(
      /<(span|b|strong|em|i|u|s)(?:\s[^>]*)?>[\s\u00a0]*(?:&nbsp;[\s\u00a0]*)*<\/\1>/gi,
      "",
    );
  }

  return next;
}

function addChecklistLineBreaks(html: string) {
  const checkmarkCount = (html.match(/[✔✓]/g) || []).length;
  if (checkmarkCount < 2 && !/^[\s\u00a0]*(?:<[^>]+>[\s\u00a0]*)*[✔✓]/.test(html)) return html;

  return html
    .replace(/((?:<\/?(?:span|b|strong|em|i|u|s)(?:\s[^>]*)?>[\s\u00a0]*)*)([✔✓]\s*)/gi, "<br />$1$2")
    .replace(/^(?:\s|<br\s*\/?>)+/i, "");
}

function wrapLooseInlineHtml(html: string) {
  const normalized = cleanEmptyInlineTags(html.replace(/\r\n?/g, "\n"));
  const parts = normalized.split(/\n[ \t\u00a0]*(?:\n[ \t\u00a0]*)+/);

  return parts
    .map((part) => addChecklistLineBreaks(part.trim()))
    .filter(Boolean)
    .map((part) => `<p>${part}</p>`)
    .join("");
}

export function restoreLegacyParagraphs(html: string) {
  let output = "";
  let lastIndex = 0;
  let index = 0;

  const blockTags = new Set(["p", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "blockquote", "figure", "table", "div", "section", "details"]);

  while (index < html.length) {
    // Look for a tag start
    const tagMatch = html.slice(index).match(/^<([a-zA-Z0-9:-]+)\b/);
    if (tagMatch) {
      const tagName = tagMatch[1].toLowerCase();
      if (blockTags.has(tagName)) {
        // We found a block tag! Let's find its matching closing tag.
        const startTagIndex = index;
        let depth = 1;
        let searchIndex = index + tagMatch[0].length;

        // Skip to end of opening tag
        const tagEndMatch = html.slice(searchIndex).match(/^[^>]*>/);
        if (tagEndMatch) {
          searchIndex += tagEndMatch[0].length;
        }

        while (searchIndex < html.length && depth > 0) {
          const nextTag = html.slice(searchIndex).match(/<(\/)?([a-zA-Z0-9:-]+)\b/);
          if (!nextTag) {
            break;
          }

          const isClosing = Boolean(nextTag[1]);
          const currentTagName = nextTag[2].toLowerCase();

          if (currentTagName === tagName) {
            if (isClosing) {
              depth--;
            } else {
              depth++;
            }
          }

          // Advance searchIndex to the end of this tag
          const tagCloseEnd = html.slice(searchIndex + nextTag.index!).match(/^[^>]*>/);
          searchIndex += nextTag.index! + (tagCloseEnd ? tagCloseEnd[0].length : nextTag[0].length);
        }

        // We matched the block element from startTagIndex to searchIndex
        output += wrapLooseInlineHtml(html.slice(lastIndex, startTagIndex));
        output += html.slice(startTagIndex, searchIndex);
        lastIndex = searchIndex;
        index = searchIndex;
        continue;
      }
    }

    // Check for self-closing/void block elements like <hr> or <iframe>
    const voidMatch = html.slice(index).match(/^<(hr|iframe)\b[^>]*>/i);
    if (voidMatch) {
      let endIdx = index + voidMatch[0].length;
      if (voidMatch[1].toLowerCase() === "iframe") {
        // If iframe is not self-closing, find its </iframe>
        const iframeClose = html.slice(endIdx).match(/<\/iframe>/i);
        if (iframeClose) {
          endIdx += iframeClose.index! + iframeClose[0].length;
        }
      }
      output += wrapLooseInlineHtml(html.slice(lastIndex, index));
      output += html.slice(index, endIdx);
      lastIndex = endIdx;
      index = endIdx;
      continue;
    }

    index++;
  }

  output += wrapLooseInlineHtml(html.slice(lastIndex));
  return output;
}
